/** "Work with Dockie": a public signup for vendors. One kind, one short form, then the papers.
 *  Nothing goes live until Dockie's team says yes. */
import React, { useState } from "react";
import { ArrowLeft, Building2, CheckCircle2, FileCheck2, Search, ShieldCheck, Ship, Truck, Warehouse, Wrench } from "lucide-react";
import Logo from "@/assets/Logo.svg";
import { applyAsVendor, useVendorForms } from "@/services/python-api/customerHooks";

const ICONS = {
  forwarder: Ship,
  carrier: Truck,
  yard: Warehouse,
  clearing_agent: ShieldCheck,
  buyers_agent: Search,
  title_service: FileCheck2,
  inspector: Wrench,
};

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <header className="flex h-16 items-center border-b border-[#E4E7EC] bg-white px-5">
        <img src={Logo} alt="Dockie" className="h-7" />
      </header>
      <main className="mx-auto w-full max-w-[720px] px-4 py-8">{children}</main>
    </div>
  );
}

function Input({ label, hint, required, ...props }) {
  return (
    <label className="block">
      <span className="text-[13px] font-medium text-[#344054]">
        {label}
        {required ? <span className="text-[#CB1A14]"> *</span> : null}
      </span>
      <input
        required={required}
        {...props}
        placeholder={hint || props.placeholder}
        className="mt-1.5 h-11 w-full rounded-xl border border-[#D0D5DD] bg-white px-3 text-[15px] outline-none focus:border-[#2563EB]"
      />
    </label>
  );
}

export default function WorkWithDockie() {
  const { data, isLoading, isError } = useVendorForms();
  const kinds = data?.kinds ?? [];
  const [kind, setKind] = useState(null);
  const [fields, setFields] = useState({ company: "", contact_name: "", email: "", phone: "", country: "" });
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState({});
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const form = kinds.find((k) => k.kind === kind);

  if (done) {
    return (
      <Shell>
        <div className="rounded-3xl border border-[#E4E7EC] bg-white p-8 text-center">
          <CheckCircle2 size={44} color="#16A34A" className="mx-auto" />
          <h1 className="mt-4 text-[24px] font-semibold text-[#101928]">Thanks, {fields.contact_name.split(" ")[0]}</h1>
          <p className="mt-2 text-[15px] text-[#475367]">
            Dockie's team is checking your papers. We'll email {fields.email} when you're set up. Sign in with that
            email any time to see where it stands.
          </p>
        </div>
      </Shell>
    );
  }

  if (!form) {
    return (
      <Shell>
        <h1 className="text-[28px] font-semibold tracking-tight text-[#101928]">Work with Dockie</h1>
        <p className="mt-2 text-[16px] text-[#475367]">
          We move cars from US auctions to Lagos. Tell us what you do, and we'll be in touch.
        </p>
        {isLoading ? <p className="mt-8 text-[#98A2B3]">Loading…</p> : null}
        {isError ? <p className="mt-8 text-[#CB1A14]">Signups aren't open right now.</p> : null}
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          {kinds.map((k) => {
            const Icon = ICONS[k.kind] || Building2;
            return (
              <button
                key={k.kind}
                onClick={() => setKind(k.kind)}
                className="flex items-start gap-3 rounded-2xl border border-[#E4E7EC] bg-white p-4 text-left transition hover:border-[#2563EB] hover:shadow-sm"
              >
                <span className="rounded-xl bg-[#EFF6FF] p-2.5">
                  <Icon size={20} color="#2563EB" />
                </span>
                <span>
                  <span className="block text-[15px] font-semibold text-[#101928]">{k.label}</span>
                  <span className="mt-0.5 block text-[13px] leading-snug text-[#667185]">{k.blurb}</span>
                </span>
              </button>
            );
          })}
        </div>
      </Shell>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError(null);
    try {
      const picked = Object.entries(files).map(([k, file]) => ({ kind: k, file }));
      await applyAsVendor({ ...fields, kind, answers }, picked);
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Shell>
      <button className="flex items-center gap-1 text-[14px] text-[#2563EB]" onClick={() => setKind(null)}>
        <ArrowLeft size={16} /> All kinds
      </button>
      <h1 className="mt-3 text-[26px] font-semibold tracking-tight text-[#101928]">{form.label}</h1>
      <p className="mt-1 text-[15px] text-[#475367]">{form.blurb}</p>
      <form onSubmit={submit} className="mt-6 space-y-6">
        <section className="space-y-4 rounded-2xl border border-[#E4E7EC] bg-white p-5">
          <p className="text-[15px] font-semibold text-[#101928]">About you</p>
          <Input label="Company" required value={fields.company} onChange={(e) => setFields({ ...fields, company: e.target.value })} />
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label="Your name" required value={fields.contact_name} onChange={(e) => setFields({ ...fields, contact_name: e.target.value })} />
            <Input label="Work email" type="email" required value={fields.email} onChange={(e) => setFields({ ...fields, email: e.target.value })} />
            <Input label="Phone" value={fields.phone} onChange={(e) => setFields({ ...fields, phone: e.target.value })} />
            <Input label="Country" value={fields.country} onChange={(e) => setFields({ ...fields, country: e.target.value })} />
          </div>
        </section>
        <section className="space-y-4 rounded-2xl border border-[#E4E7EC] bg-white p-5">
          <p className="text-[15px] font-semibold text-[#101928]">Your work</p>
          {form.questions.map((q) =>
            q.type === "yesno" ? (
              <div key={q.key}>
                <p className="text-[13px] font-medium text-[#344054]">{q.label}</p>
                <div className="mt-1.5 flex gap-2">
                  {["Yes", "No"].map((v) => (
                    <button
                      type="button"
                      key={v}
                      onClick={() => setAnswers({ ...answers, [q.key]: v })}
                      className={`h-10 w-20 rounded-xl border text-[14px] ${
                        answers[q.key] === v ? "border-[#2563EB] bg-[#EFF6FF] text-[#1D4ED8]" : "border-[#D0D5DD]"
                      }`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <Input
                key={q.key}
                label={q.label}
                hint={q.hint}
                required={q.required}
                type={q.type === "number" ? "number" : "text"}
                value={answers[q.key] || ""}
                onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.value })}
              />
            )
          )}
        </section>
        <section className="space-y-3 rounded-2xl border border-[#E4E7EC] bg-white p-5">
          <p className="text-[15px] font-semibold text-[#101928]">Papers</p>
          {form.documents.map((d) => (
            <label
              key={d.key}
              className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-dashed border-[#D0D5DD] px-4 py-3 hover:bg-[#F9FAFB]"
            >
              <span>
                <span className="block text-[14px] font-medium text-[#101928]">
                  {d.label}
                  {d.required ? <span className="text-[#CB1A14]"> *</span> : null}
                </span>
                <span className="block text-[12px] text-[#667185]">{files[d.key]?.name || "PDF or photo"}</span>
              </span>
              <span className="text-[14px] font-medium text-[#2563EB]">{files[d.key] ? "Change" : "Add"}</span>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.heic"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && setFiles({ ...files, [d.key]: e.target.files[0] })}
              />
            </label>
          ))}
        </section>
        {error ? <p className="text-[14px] text-[#CB1A14]">{error}</p> : null}
        <button
          disabled={sending}
          className="h-12 w-full rounded-full bg-[#2563EB] text-[16px] font-medium text-white disabled:opacity-60"
        >
          {sending ? "Sending…" : "Apply"}
        </button>
        <p className="text-center text-[13px] text-[#98A2B3]">Dockie's team checks every application before anything goes live.</p>
      </form>
    </Shell>
  );
}
